import {
  HealthCheckDialog,
  type HealthCheckDialogProps,
} from '@/components/health-check-dialog.component';
import { MAINTENANCE_FLAG } from '@/components/maintenance-gate.component';
import { getApiBase } from '@/lib/api-base';
import { ORGANIZATION_SUSPENDED_EVENT } from '@/store/base-query';
import posthog from 'posthog-js';
import ReactDOM from 'react-dom/client';

/** Consecutive failed checks, 10s apart, before "Connection Lost" shows. */
export const MAX_FAILURES = 3;
export const CHECK_INTERVAL_MS = 10_000;

/**
 * Polls /api/v1/health and raises the "Connection Lost" dialog when the API
 * stops answering. Replay showed most of those dialogs were false alarms the
 * user dismissed and worked straight past, so:
 *
 * - A hidden tab isn't checked, and returning to it starts the count over: a
 *   laptop waking up fails its first fetch while Wi-Fi reconnects.
 * - Any answer from the API (a 429 included) is "reachable". Only a failed
 *   fetch or a 5xx counts.
 * - Polling keeps going under the dialog and closes it once the API answers,
 *   instead of leaving it up until the user reloads.
 */
export function startHealthCheck(): void {
  let failCount = 0;
  let closeDialog: (() => void) | null = null;
  let restricted = false;

  function showDialog(opts: HealthCheckDialogProps): () => void {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const dialogRoot = ReactDOM.createRoot(container);
    const close = () => {
      dialogRoot.unmount();
      container.remove();
      closeDialog = null;
    };
    const onDismiss = opts.onDismiss;
    dialogRoot.render(
      <HealthCheckDialog
        {...opts}
        onDismiss={
          onDismiss
            ? () => {
                close();
                onDismiss();
              }
            : undefined
        }
      />
    );
    return close;
  }

  // Shown once, for good: nothing in the app works until access returns, and
  // a reload checks again.
  function restrict(description?: string) {
    if (restricted) return;
    restricted = true;
    closeDialog?.();
    showDialog({
      kind: 'account-restricted',
      description,
      onPrimaryAction: () => {
        window.location.href =
          'mailto:support@cleanlist.ai?subject=Account%20Restricted';
      },
    });
  }

  window.addEventListener(ORGANIZATION_SUSPENDED_EVENT, () =>
    restrict(
      'This organization has been suspended. Contact support to restore access.'
    )
  );

  async function checkHealth() {
    if (restricted) return;
    if (document.visibilityState === 'hidden') {
      failCount = 0;
      setTimeout(checkHealth, CHECK_INTERVAL_MS);
      return;
    }
    try {
      // getApiBase() strips the trailing slash. In dev VITE_API_URL is "/"
      // (relative, so requests ride the dev proxy), and the previous naive
      // concatenation produced "//api/v1/health" — which the browser resolves
      // as PROTOCOL-RELATIVE, i.e. http://api/v1/health, a host that does not
      // exist. Every check failed and raised the "Connection Lost" dialog on a
      // perfectly healthy stack.
      const response = await fetch(`${getApiBase()}/api/v1/health`);
      if (response.status === 403) {
        // IP / account banned by scanner blocklist or rate limiter — show
        // the restricted-account dialog and stop polling (no point retrying;
        // ban TTL is hours-to-days).
        restrict();
        return;
      }
      if (response.status >= 500) {
        throw new Error('Healthcheck failed');
      }
      failCount = 0;
      closeDialog?.();
    } catch {
      failCount++;
      // The outage may be planned: re-read flags so MaintenanceGate, not this
      // dialog, takes over while maintenance_mode_v1 is on.
      posthog.reloadFeatureFlags();
      if (
        failCount >= MAX_FAILURES &&
        !closeDialog &&
        !posthog.isFeatureEnabled(MAINTENANCE_FLAG)
      ) {
        closeDialog = showDialog({
          kind: 'connection-lost',
          onPrimaryAction: () => window.location.reload(),
          onDismiss: () => {
            failCount = 0;
          },
        });
      }
    }
    setTimeout(checkHealth, CHECK_INTERVAL_MS);
  }

  // Start health check after a short delay
  setTimeout(checkHealth, 5000);
}
